import axios, { AxiosError, AxiosRequestConfig, Method } from "axios";
import { Observable, Subscriber } from "rxjs";
import { logD, logE } from "@blacknut/logging/dist";
import { ApiError } from "../model";

interface RequestParams {
  method?: Method;
  path: string;
  body?: unknown;
  params?: Record<string, string | undefined | number>;
  headers?: Record<string, string>;
}

const DEFAULT_ENDPOINT = "https://www.blacknutlemag.com/api";
const TAG = "LeMag";

class LeMagService {
  private _locale: string | undefined;
  public get locale(): string | undefined {
    return this._locale;
  }
  public set locale(v: string | undefined) {
    this._locale = v;
  }
  private _endpoint = DEFAULT_ENDPOINT;
  public get endpoint(): string {
    return this._endpoint;
  }
  public set endpoint(v: string) {
    this._endpoint = v;
  }

  private getDefaultAxiosConfig(
    headers?: Record<string, string>,
    params?: Record<string, string | number | undefined>,
  ): AxiosRequestConfig {
    const _headers: Record<string, string> = {
      "Accept-Language": this.locale || "en-US",
      ...headers,
    };

    return {
      headers: _headers,
      params,
      responseType: "json",
    };
  }

  public cleanup() {
    this.endpoint = DEFAULT_ENDPOINT;
    this._locale = undefined;
  }

  public get<T>(params: RequestParams) {
    return this.request<T>({
      ...params,
      method: "GET",
    });
  }

  private handleError<T>(
    error: AxiosError<{ errors: ApiError[] }>,
    observer: Subscriber<T>,
  ) {
    logE(TAG, "In handle error: %o", error);
    if (
      error.response?.data &&
      error.response?.data.errors &&
      error.response?.data.errors.length > 0
    ) {
      const _error = error.response?.data.errors[0];
      observer.error(this.wrapError(_error));
      observer.complete();
    } else {
      observer.error(
        this.wrapError({
          code: "unknown",
          status: "",
          title: error.message,
        }),
      );
      observer.complete();
    }
  }

  private wrapError(_error: ApiError) {
    return {
      ..._error,
      toString: () => {
        return _error.title;
      },
    };
  }

  public request<T>({ method, path, body, params, headers }: RequestParams) {
    return new Observable<T>((observer) => {
      logD(TAG, "%o %o", method, path);
      const request = () => {
        return axios
          .request<T>({
            ...this.getDefaultAxiosConfig(headers, params),
            method,
            url: `${this.endpoint}/${this.locale}/${path}`,
            data: body,
          })
          .then((res) => res.data);
      };
      request()
        .then((data) => {
          observer.next(data);
          observer.complete();
        })
        .catch((e) => {
          this.handleError(e, observer);
        });
    });
  }

  public post<T>(params: RequestParams) {
    return this.request<T>({
      ...params,
      method: "POST",
    });
  }

  public put<T>(params: RequestParams) {
    return this.request<T>({
      ...params,
      method: "PUT",
    });
  }

  public deleteApi<T>(params: RequestParams) {
    return this.request<T>({
      ...params,
      method: "DELETE",
    });
  }
}

export const leMagService = new LeMagService();
export default leMagService;
